"use client";

import { useEffect, useRef, useState } from "react";

import { CLEARANCE } from "./panel-marks";
import { overlaps, type Seat } from "./seats";

/** Honours the OS setting: with motion reduced, the marks simply stay put. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => {
      setReduced(event.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);
  return reduced;
}

const rand = (min: number, span: number) => min + Math.random() * span;

function shuffled(count: number): number[] {
  const out = Array.from({ length: count }, (_, i) => i);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j] as number, out[i] as number];
  }
  return out;
}
const pick = <T>(list: T[]): T | null =>
  list.length ? (list[Math.floor(Math.random() * list.length)] as T) : null;

export interface Pop {
  slot: number;
  /** Index into the caller's mark list. */
  mark: number;
  seat: Seat;
  shown: boolean;
  /** Bumped every cycle so the caller can key off it and force a remount. */
  cycle: number;
}

/**
 * Runs `slots` marks at a time, each choosing a fresh mark and a fresh seat on
 * every cycle, so neither the order nor the place on the map repeats. A slot
 * never takes the mark the other slot is showing, and never a seat that would
 * collide with it.
 */
export function useRandomPops(
  count: number,
  seatsForMark: (mark: number) => Seat[],
  ready: boolean,
  reduced: boolean,
  slots = 2,
): Pop[] {
  const [pops, setPops] = useState<Pop[]>([]);
  // Mirrors state so a timer can see what the other slot is doing without
  // re-running the effect on every change.
  const live = useRef<(Pop | null)[]>([]);
  // A slot is emptied before it picks again, so "not the one I just showed"
  // cannot be read off `live`: the guard that used to live here never fired and
  // a three-mark set could show the same one several times running. Draw from a
  // shuffled bag instead, so every mark appears once per round.
  const bag = useRef<number[]>([]);
  const lastMark = useRef<number | null>(null);
  const seats = useRef(seatsForMark);
  seats.current = seatsForMark;

  useEffect(() => {
    if (!ready || count < 1) {
      return;
    }
    const total = Math.min(slots, count);
    live.current = new Array(total).fill(null);
    bag.current = [];
    lastMark.current = null;
    const publish = () => {
      setPops(live.current.filter((p): p is Pop => p !== null));
    };

    const choose = (slot: number, cycle: number): Pop | null => {
      const others = live.current.filter(
        (p, i) => i !== slot && p !== null,
      ) as Pop[];
      const taken = new Set(others.map((p) => p.mark));
      if (bag.current.length === 0) {
        bag.current = shuffled(count);
        // Do not open a round with the mark that closed the last one.
        if (count > 1 && bag.current[0] === lastMark.current) {
          bag.current.push(bag.current.shift() as number);
        }
      }
      const index = bag.current.findIndex((i) => !taken.has(i));
      if (index === -1) {
        return null;
      }
      const mark = bag.current.splice(index, 1)[0] as number;
      lastMark.current = mark;
      const free = seats.current(mark).filter((s) =>
        others.every(
          (o) =>
            s.city !== o.seat.city &&
            Math.hypot(s.pin.x - o.seat.pin.x, s.pin.y - o.seat.pin.y) >=
              CLEARANCE.pinSeparation &&
            !overlaps(s.rect, {
              x: o.seat.rect.x - CLEARANCE.mark,
              y: o.seat.rect.y - CLEARANCE.mark,
              width: o.seat.rect.width + 2 * CLEARANCE.mark,
              height: o.seat.rect.height + 2 * CLEARANCE.mark,
            }),
        ),
      );
      const seat = pick(free);
      return seat ? { slot, mark, seat, shown: false, cycle } : null;
    };

    if (reduced) {
      for (let slot = 0; slot < total; slot++) {
        const next = choose(slot, 0);
        if (next) {
          live.current[slot] = { ...next, shown: true };
        }
      }
      publish();
      return;
    }

    const timers = new Map<number, number>();
    const run = (slot: number, cycle: number) => {
      const next = choose(slot, cycle);
      if (!next) {
        // No seat free right now; try again once the other slot has moved.
        timers.set(
          slot,
          window.setTimeout(() => {
            run(slot, cycle);
          }, 700),
        );
        return;
      }
      live.current[slot] = next;
      publish();
      timers.set(
        slot,
        window.setTimeout(() => {
          live.current[slot] = { ...next, shown: true };
          publish();
          timers.set(
            slot,
            window.setTimeout(
              () => {
                live.current[slot] = { ...next, shown: false };
                publish();
                timers.set(
                  slot,
                  window.setTimeout(() => {
                    live.current[slot] = null;
                    publish();
                    timers.set(
                      slot,
                      window.setTimeout(
                        () => {
                          run(slot, cycle + 1);
                        },
                        rand(400, 900),
                      ),
                    );
                  }, 420),
                );
              },
              rand(3600, 2400),
            ),
          );
        }, 60),
      );
    };

    for (let slot = 0; slot < total; slot++) {
      timers.set(
        slot,
        window.setTimeout(
          () => {
            run(slot, 0);
          },
          400 + slot * 1500,
        ),
      );
    }
    return () => {
      for (const id of timers.values()) {
        window.clearTimeout(id);
      }
    };
  }, [count, ready, reduced, slots]);

  return pops;
}
